import type { GQL_ID } from '@@partner:types:graphql'
import type { CrudFormProps } from '@@partner:types:app'
import type { BannerTypeFormState } from './BannerTypeFormState'

import { mergeLeft, mergeLeftOnly } from 'merge-left-utils'
import { useValidatableForm } from '@@partner:helpers/hooks/rsuite'

import React, { useState, useCallback, useEffect, useMemo } from 'react'
import { useFetchSingleBannerType } from '@@partner:graphql/query/banners/singleBannerType.gql'
import { useMutateSaveBannerType } from '@@partner:graphql/mutation/banners/saveBannerType.gql'

import {
  notifyOk,
  makeGraphQLErrorNotification,
  notifyError
} from '../../../../helpers/notification'
import {
  makeBannerTypeFormState,
  mapApiDataToState,
  mapStateToApiVariable
} from './BannerTypeFormState'

import { Form, Grid, Panel, Row, Col } from 'rsuite'
import { SaveButton } from '@components/common/form/Buttons'
import GraphQLCommonErrorDisplay from '@components/common/GraphQLCommonErrorDisplay'

export interface BannerTypeFormProps extends CrudFormProps {
  bannerTypeId: GQL_ID
}

export default function BannerTypeForm ({
  bannerTypeId,
  onSaved
}: BannerTypeFormProps) {
  const [ formError, setFormError ] = useState({})
  const { formState, setFormState, setFormRef } =
    useValidatableForm<BannerTypeFormState>(makeBannerTypeFormState)

  const {
    data,
    loading: loadingBannerType,
    error: errorLoadingBannerType
  } = useFetchSingleBannerType({
    id: bannerTypeId
  })

  const [ saveBannerType, { loading: loadingSaveBannerType } ] =
    useMutateSaveBannerType()

  useEffect(() => {
    if (!loadingSaveBannerType && data) {
      setFormState((state) =>
        mergeLeft(state, mapApiDataToState(data?.bannerType))
      )
    }
  }, [ data, loadingSaveBannerType, setFormState ])

  const handleFormError = useCallback((error: any) => {
    setFormError(error)
  }, [])

  const handleFormChange = useCallback(
    (formData: any) => {
      setFormState((state) => mergeLeft(state, formData))
    },
    [ setFormState ]
  )

  const handleFormSubmit = useCallback(
    (isOk = false) => {
      if (isOk) {
        saveBannerType({
          variables: {
            newData: mapStateToApiVariable(formState),
            upsert: formState.id ? false : true
          }
        })
          .then(({ data }) => {
            notifyOk('Тип баннера сохранён')
            setFormState((state) =>
              mergeLeftOnly([ 'id' ], state, data?.bannerTypeInfo)
            )

            onSaved?.(data?.bannerTypeInfo.id)
          })
          .catch(makeGraphQLErrorNotification('Ошибка сохранения данных'))
      }
      else {
        notifyError('Форма заполнена некорректно')
      }
    },
    [ saveBannerType, formState, setFormState, onSaved ]
  )

  if (errorLoadingBannerType) {
    return (
      <GraphQLCommonErrorDisplay
        title={`Не удалось загрузить тип баннера #${bannerTypeId} для редактирования`}
        error={errorLoadingBannerType}
      />
    )
  }

  return (
    <Form
      formValue={formState}
      onChange={handleFormChange}
      onSubmit={handleFormSubmit}
      onCheck={handleFormError}
      ref={setFormRef}
      fluid
    >
      <Panel className="mb-4">
        <Grid fluid>
          <Row>
            <Col md={15}>
              <Form.Group>
                <Form.ControlLabel>Тип</Form.ControlLabel>
                <Form.Control name="label" placeholder="Название" />
              </Form.Group>

              <Form.Group>
                <Form.ControlLabel>Код типа</Form.ControlLabel>
                <Form.Control name="code" placeholder="Название" />
              </Form.Group>
            </Col>
          </Row>
        </Grid>
      </Panel>
      <SaveButton
        loading={loadingBannerType || loadingSaveBannerType}
        disabled={loadingBannerType || loadingSaveBannerType}
      >
        Сохранить
      </SaveButton>
    </Form>
  )
}
